import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { changePasswordRequestSchema } from '@perplexicon/validation';
import type { ChangePasswordRequest } from '@perplexicon/validation';

import type { AuthSessionInfo } from '@perplexicon/api-client';

import {
  Button,
  Card,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  PageLayout,
  StatusMessage,
  Tabs,
} from '../../components/index.js';
import {
  useAuthSessions,
  useChangePassword,
  useCurrentUser,
  useLogoutAll,
} from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';
import { formatDateTime } from '../../lib/format-date.js';
import { HistoryPanel } from '../history/HistoryPage.js';
import { AvatarEditor } from './AvatarEditor.js';
import { ProfileCustomizationEditor } from './ProfileCustomizationEditor.js';

/** Phone/tablet/computer, in the words a person would use about their own devices. */
const DEVICE_LABELS: Record<AuthSessionInfo['deviceType'], string> = {
  mobile: 'Phone',
  tablet: 'Tablet',
  desktop: 'Computer',
  bot: 'Automated client',
  unknown: 'Unrecognized device',
};

/**
 * Where the account is currently signed in (CLAUDE.md §9). Everything shown
 * here comes from `GET /api/auth/sessions`, which is scoped server-side to
 * the caller's own sessions and deliberately carries no IP address, no
 * address pseudonym, and no session token — only enough for someone to
 * recognize their own devices and notice one they do not.
 */
function ActiveSessionsList() {
  const { data: user } = useCurrentUser();
  const sessions = useAuthSessions(!!user);

  if (sessions.isPending) return <LoadingState label="Loading sessions…" />;
  if (sessions.isError) {
    return (
      <ErrorState
        message={getErrorMessage(sessions.error)}
        onRetry={() => void sessions.refetch()}
      />
    );
  }
  if (sessions.data.length === 0) {
    return <p className="mt-3 text-sm text-textSecondary">No other sessions are signed in.</p>;
  }

  return (
    <ul className="mt-3 flex flex-col gap-2">
      {sessions.data.map((session) => (
        <li
          key={session.id}
          className="flex flex-col gap-1 rounded-md border border-border p-3 text-sm tablet:flex-row tablet:items-center tablet:justify-between"
        >
          <div className="min-w-0">
            <p className="font-medium text-textPrimary">
              {session.browserFamily} on {session.osFamily}
              {session.isCurrent && (
                <span className="ml-2 text-xs font-semibold text-accent">This device</span>
              )}
            </p>
            <p className="text-xs text-textSecondary">{DEVICE_LABELS[session.deviceType]}</p>
          </div>
          <div className="shrink-0 text-xs text-textSecondary tablet:text-right">
            <p>Signed in {formatDateTime(session.createdAt)}</p>
            <p>Last active {formatDateTime(session.lastActiveAt)}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function AccountSection() {
  const { data: user } = useCurrentUser();
  const changePassword = useChangePassword();
  const logoutAll = useLogoutAll();
  const {
    register: field,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordRequest>({ resolver: zodResolver(changePasswordRequestSchema) });

  const onSubmit = handleSubmit((values) => {
    changePassword.mutate(values, { onSuccess: () => reset() });
  });

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <h2 className="text-lg font-medium text-textPrimary">Account</h2>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-textSecondary">Username</dt>
          <dd className="text-textPrimary">{user?.username}</dd>
          <dt className="text-textSecondary">Email</dt>
          <dd className="text-textPrimary">{user?.email}</dd>
        </dl>
      </Card>

      <Card>
        <h2 className="text-lg font-medium text-textPrimary">Change password</h2>
        <form onSubmit={(e) => void onSubmit(e)} noValidate className="mt-4 flex flex-col gap-4">
          <Input
            label="Current password"
            type="password"
            autoComplete="current-password"
            error={errors.currentPassword?.message}
            {...field('currentPassword')}
          />
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            hint="At least 12 characters."
            error={errors.newPassword?.message}
            {...field('newPassword')}
          />
          {changePassword.isError && <ErrorState message={getErrorMessage(changePassword.error)} />}
          {changePassword.isSuccess && (
            <StatusMessage tone="success">Password changed.</StatusMessage>
          )}
          <Button type="submit" isLoading={changePassword.isPending} className="self-start">
            Change password
          </Button>
        </form>
      </Card>

      <Card>
        <h2 className="text-lg font-medium text-textPrimary">Sessions</h2>
        <p className="mt-2 text-sm text-textSecondary">
          Where your account is currently signed in. If you don&apos;t recognize one of these, sign
          out everywhere and change your password.
        </p>
        <ActiveSessionsList />
        {logoutAll.isError && <ErrorState message={getErrorMessage(logoutAll.error)} />}
        <Button
          variant="secondary"
          isLoading={logoutAll.isPending}
          className="mt-3"
          onClick={() => logoutAll.mutate()}
        >
          Log out everywhere
        </Button>
      </Card>
    </div>
  );
}

const TAB_VALUES = ['account', 'avatar', 'profile', 'history'] as const;
type SettingsTab = (typeof TAB_VALUES)[number];

function isSettingsTab(value: string | null): value is SettingsTab {
  return value !== null && (TAB_VALUES as readonly string[]).includes(value);
}

/**
 * The account settings screen (APP_SPEC §65 "Platform"). Uses the same
 * tabbed sub-navigation pattern as the Friends interface
 * (`components/Tabs.tsx`). Blocked Users now lives under Friends
 * (docs/UI_GUIDELINES.md §5); Game History is the third tab here rather
 * than a primary-navigation destination.
 */
export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: SettingsTab = isSettingsTab(tabParam) ? tabParam : 'account';

  const setTab = (next: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'account') params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  };

  return (
    // Every tab shares the shell's default width now — the narrower
    // `readable` width the non-Profile tabs previously used read as too
    // small in practice, once actually looked at rendered rather than
    // judged by its own comment.
    <PageLayout>
      <PageHeader
        eyebrow="Your account"
        title="Settings"
        description="Manage account details, security, your avatar, profile appearance, and game history."
      />
      <Tabs
        aria-label="Settings"
        value={tab}
        onValueChange={setTab}
        items={[
          { value: 'account', label: 'Account', content: <AccountSection /> },
          { value: 'avatar', label: 'Avatar', content: <AvatarEditor /> },
          { value: 'profile', label: 'Profile', content: <ProfileCustomizationEditor /> },
          { value: 'history', label: 'Game History', content: <HistoryPanel /> },
        ]}
      />
    </PageLayout>
  );
}
